import React from "react";
import ReactDOMServer from "react-dom/server";
import { observer } from "mobx-react";
import ModelWalkStore from "../../stores/ModelWalkStore";
import { ContentImagePath } from "../../utils/ContentMediaPath";
import { VariantModel, VariantFeature } from "../../models/VariantModel";
import { ModelBasePath } from "../../utils/ModelBasePath";
import { ModelName } from "../../utils/ModelName";
import * as Request from "superagent";
import { formatMoney } from "accounting";
import Repayment from "../Repayments/Repayment";
import ImgLazyLoad from "../ImgLazyLoad";

interface ModelWalkCardState {}

interface ModelWalkCardProps {
  carDetails: VariantModel;
  className?: string;
  leftPos: number;
  comparisonMode?: boolean;
  store: ModelWalkStore;
  enableStaticPricingByState?: boolean;
  allVariantPriceHidden?: boolean;
  hideRepayment: boolean;
}

@observer
class ModelWalkCard extends React.Component<
  ModelWalkCardProps,
  ModelWalkCardState
> {
  constructor(props: ModelWalkCardProps) {
    super(props);
    this.keyFeatures = this.keyFeatures.bind(this);
  }

  keyFeatures(className?: string) {
    const { carDetails } = this.props;
    return (
      <ul className={`modelWalk__keyFeatures ${className}`}>
        {carDetails.keyFeatures.map((feature: VariantFeature, id: number) => {
          let htmlContent = feature.label;

          if (feature.disclaimer) {
            htmlContent = `${htmlContent}<span class="tt">*<span class="tt__content">${feature.disclaimer}</span></span>`;
          }
          return (
            <li className="modelWalk__keyFeaturesItem" key={id}>
              <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
            </li>
          );
        })}
      </ul>
    );
  }

  renderCtaPanel = () => {
    const { carDetails } = this.props;

    if (
      carDetails.hideBuildYourOwnButton &&
      carDetails.hideSpecsAndFeaturesButton
    )
      return;

    return (
      <div className="modelWalk__copy modelWalk__copy--cta">
        {!carDetails.hideBuildYourOwnButton && (
          <p>
            <a
              href={carDetails.byoButtonURLOverride}
              target={carDetails.byoButtonTargetOverride}
              className="btn btn--primary modelWalk__order"
            >
              <span>{carDetails.byoButtonTextOverride}</span>
            </a>
          </p>
        )}

        {!carDetails.hideSpecsAndFeaturesButton && (
          <a
            href={
              carDetails.overrideSpecsAndFeaturesButton
                ? carDetails.overrideSpecsAndFeaturesButton.url
                : `${ModelBasePath}specs`
            }
            target={
              carDetails.overrideSpecsAndFeaturesButton
                ? carDetails.overrideSpecsAndFeaturesButton.target
                : `_self`
            }
          >
            {carDetails.overrideSpecsAndFeaturesButton
              ? carDetails.overrideSpecsAndFeaturesButton.name
              : "View full Specs & Features"}
          </a>
        )}
      </div>
    );
  };

  render() {
    const {
      store,
      className,
      carDetails,
      comparisonMode,
      allVariantPriceHidden,
      enableStaticPricingByState,
    } = this.props;

    return (
      <article
        className={`modelWalk__model ${className} ${
          comparisonMode ? "modelWalk__model--compare" : ""
        } ${carDetails.highlightVariant ? "model-walk-box-shadow" : ""}`}
      >
        <div className="modelWalk__imageWrap">
          <ImgLazyLoad
            className="modelWalk__image"
            src={carDetails.image}
            alt={carDetails.name}
          />
        </div>

        <div
          className={`modelWalk__copy modelWalk__modelTitleContainer ${
            !allVariantPriceHidden &&
            carDetails.hidePricingNotAvailable &&
            "modelWalk__copy--padding"
          }`}
        >
          <h2 className="modelWalk__modelTitle">{carDetails.name}</h2>
        </div>
        {!carDetails.hidePriceDetails || enableStaticPricingByState ? (
          <div className="modelWalk__pricing">
            <span className="modelWalk__pricing-from">From</span>
            <div
              className={`modelWalk__price inlineLoader ${
                carDetails.rdp ? "" : "inlineLoader--loading"
              }`}
            >
              <span className="inlineLoader__loader" />
              {carDetails.rdp ? (
                <div>
                  {formatMoney(carDetails.rdp, {
                    symbol: "$",
                    precision: 0,
                  })}
                </div>
              ) : (
                <div>&nbsp;</div>
              )}
            </div>
            <div className="modelWalk__detailedLink">
              <a
                onClick={(e) => {
                  e.preventDefault();
                  store.showDetailedPricing(carDetails);
                }}
              >
                Recommended Driveaway Price
                <i className="icon icon--info" />
              </a>
            </div>
            {this.props.hideRepayment ? (
              <p></p>
            ) : (
              <Repayment
                pimIdentifier={carDetails.pimIdentifier}
                rdp={carDetails.rdp}
              />
            )}
          </div>
        ) : (
          !carDetails.hidePricingNotAvailable && (
            <div
              className="modelWalk__pricing"
              onClick={(e) => {
                e.preventDefault();
                store.showCtaUrl("/dealers");
              }}
            >
              <div className="modelWalk__pricing-not-available">
                CONFIRM WITH
              </div>
              <div className="modelWalk__pricing-not-available">YOUR LOCAL</div>
              <div>
                <a
                  href="/dealers"
                  target="_blank"
                  className="modelWalk__detailedLink"
                >
                  SUBARU RETAILER
                </a>
              </div>
            </div>
          )
        )}

        {!comparisonMode ? (
          <div>
            <div className="modelWalk__featuresDisclaimer">
              {carDetails.showFeaturesDisclaimer && (
                <span>
                  The specs and features are indicative only and subject to
                  change
                </span>
              )}
            </div>

            <div className="modelWalk__copy">
              <ul className="modelWalk__features">
                {carDetails.features.map(
                  (feature: VariantFeature, id: number) => {
                    let htmlContent = feature.label.replace(
                      /®/g,
                      "<sup>®</sup>"
                    );
                    let style = {};

                    if (feature.disclaimer) {
                      htmlContent = `${htmlContent}<span class="tt">*<span class="tt__content">${feature.disclaimer}</span></span>`;
                    }

                    if (
                      htmlContent.indexOf("plus:") !== -1 ||
                      htmlContent.indexOf("including:") !== -1
                    ) {
                      style = {
                        listStyle: "none",
                        fontWeight: "700",
                        marginLeft: "-20px",
                      };
                    }

                    return (
                      <li
                        className="modelWalk__featureItem"
                        key={id}
                        style={style}
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                      />
                    );
                  }
                )}
              </ul>
            </div>
          </div>
        ) : null}

        {this.renderCtaPanel()}
      </article>
    );
  }
}

export default ModelWalkCard;
